const vw = () =>
  Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
const pick = (a) => a[Math.floor(Math.random() * a.length)];
const randint = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;

export default function buildBelt(stageEl, imageUrls, opts = {}) {
  if (!stageEl) return () => {};

  const H = opts.heightPx ?? 190;
  const laneGapX = opts.laneGapX ?? 10;
  const perLaneMin = opts.perLaneMin ?? 8;
  const perLaneMax = opts.perLaneMax ?? 16;
  const durationSecs = opts.durationSecs ?? 36;

  let metas = [];
  let mounted = true;
  let lastLayout = { laneW: 0, count: 0, leftStart: 0, w: vw() };

  const preload = (files) =>
    Promise.all(
      files.map(
        (src) =>
          new Promise((resolve) => {
            const img = new Image();
            img.onload = () =>
              resolve({
                src,
                ratio: img.naturalWidth / img.naturalHeight || 0.6,
              });
            img.onerror = () => resolve({ src, ratio: 0.6 });
            img.src = src;
          }),
      ),
    );

  const computeLanes = () => {
    const width = vw();
    const count = 4; // Exactly 4 lanes
    // To fill the screen width, let the lane width be dynamic based on screen size:
    const baseW = Math.floor((width - (count - 1) * laneGapX) / count);

    // Enforce reasonable bounds so images don't get tiny or excessively huge
    const minW = Math.round(H * 0.4);
    const maxW = Math.round(H * 2.5);
    const laneW = Math.max(minW, Math.min(maxW, baseW));

    const totalW = count * laneW + (count - 1) * laneGapX;

    // If the lanes hit the max width, they will be centered. Otherwise they fill the width.
    const leftStart = Math.max(0, Math.round((width - totalW) / 2));

    return { laneW, count, leftStart, w: width };
  };

  const build = () => {
    stageEl.textContent = "";
    const { laneW, count, leftStart, w } = computeLanes();
    lastLayout = { laneW, count, leftStart, w };

    const speedMultipliers = [0.8, 1.2, 0.5, 1.0];

    for (let lane = 0; lane < count; lane++) {
      const x = leftStart + lane * (laneW + laneGapX);
      const n = Math.max(1, randint(perLaneMin, perLaneMax));
      const base = Math.random();
      const mult = speedMultipliers[lane % 4];

      for (let i = 0; i < n; i++) {
        const meta = pick(metas);

        const el = document.createElement("div");
        el.className = "sprite";
        el.dataset.ratio = String(meta.ratio);
        el.dataset.laneW = String(laneW);
        el.dataset.laneX = String(x);

        // Let the image natural boundaries decide its size within the lane constraints
        const idealW = Math.round(H * meta.ratio);
        const wpx = Math.min(laneW, idealW);
        const hpx = wpx < idealW ? Math.round(wpx / meta.ratio) : H;

        el.style.left = `${x + Math.round((laneW - wpx) / 2)}px`;
        el.style.width = `${wpx}px`;
        el.style.height = `${hpx}px`;

        const rawPhase = (base + i / n) % 1;
        const safePhase = 0.05 + rawPhase * 0.85;
        el.style.animationDelay = `${-(safePhase * (durationSecs / mult))}s`;
        el.style.animationDuration = `${durationSecs / mult}s`;

        const img = document.createElement("img");
        img.alt = "character";
        img.src = meta.src;
        el.appendChild(img);

        stageEl.appendChild(el);
      }
    }
  };

  preload(imageUrls).then((m) => {
    if (!mounted) return;
    metas = m;
    build();
  });

  let resizeTimer = 0;

  const needsRebuild = () => {
    if (!metas.length) return false;
    const layout = computeLanes();

    const widthChanged = Math.abs(layout.w - lastLayout.w) > 8;
    const geometryChanged =
      layout.laneW !== lastLayout.laneW ||
      layout.count !== lastLayout.count ||
      layout.leftStart !== lastLayout.leftStart;

    return widthChanged && geometryChanged;
  };

  const onResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (!mounted) return;
      if (needsRebuild()) build();
      else lastLayout.w = vw();
    }, 120);
  };

  window.addEventListener("resize", onResize, { passive: true });
  window.addEventListener("orientationchange", onResize, { passive: true });

  return () => {
    mounted = false;
    window.removeEventListener("resize", onResize);
    window.removeEventListener("orientationchange", onResize);
    stageEl.textContent = "";
  };
}
